import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminService, AdminStatistics } from '../../services/admin.service';
import { AuthService } from '../../services/auth.service';
import { LogService } from '../../services/log.service';
import { CreateDiscountCouponInput, DiscountCoupon } from '../../models/coupon.model';
import { User } from '../../models/user.model';
import { CorrectionWithUser } from '../../models/correction.model';
import { Plan, SubscriptionWithDetails } from '../../models/subscription.model';
import { UserManagementComponent } from '../user-management/user-management.component';
import { UserDetailsComponent } from '../user-details/user-details.component';
import { CorrectionsManagementComponent } from '../corrections-management/corrections-management.component';
import { CorrectionReportComponent } from '../correction-report/correction-report.component';
import { PlansManagementComponent } from '../plans-management/plans-management.component';
import { SettingsComponent } from '../settings/settings.component';

type AdminView = 'overview' | 'finance' | 'users' | 'editUser' | 'corrections' | 'viewCorrection' | 'plans' | 'settings';
type AdminPrimaryView = 'overview' | 'finance' | 'users' | 'corrections' | 'plans' | 'settings';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    UserManagementComponent,
    UserDetailsComponent,
    CorrectionsManagementComponent,
    CorrectionReportComponent,
    PlansManagementComponent,
    SettingsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="min-h-screen bg-slate-100">
  <div class="mx-auto max-w-[1600px] px-4 py-6 md:px-6 md:py-8 space-y-6">
    <header class="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-900 p-6 text-white shadow-elevated">
      <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59,130,246,.15),transparent_50%)]" aria-hidden="true"></div>
      <div class="relative flex flex-wrap items-center justify-between gap-3">
        <div>
          <div class="mb-2 flex items-center gap-2">
            <span class="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg shadow-blue-900/40">
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <path d="M16.8 7.2C15.6 6 14.1 5.3 12.4 5.3C8.7 5.3 5.6 8.4 5.6 12.1C5.6 15.8 8.7 18.9 12.4 18.9C14.1 18.9 15.6 18.2 16.8 17" stroke="white" stroke-width="2" stroke-linecap="round" stroke-opacity=".35"/>
                <path d="M9.5 12.5L11.2 14.2L14.8 10.6" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="text-lg font-black tracking-tight"><span class="text-white">Correct</span><span class="text-blue-400">ify</span></span>
          </div>
          <p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-300/90">Admin</p>
          <h1 class="mt-2 text-2xl font-bold tracking-tight md:text-3xl">Central de Controle</h1>
          <p class="mt-2 text-sm text-blue-100">{{ viewHint() }}</p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button type="button" (click)="refreshAllData(true)" [disabled]="isBusy()" class="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur-sm transition hover:bg-white/20 disabled:opacity-60">
            @if (isBusy()) { Atualizando... } @else { Atualizar }
          </button>
          <button type="button" (click)="logout()" class="rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-900 transition hover:bg-blue-50">Sair</button>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <article class="rounded-2xl border border-white/15 bg-white/10 px-4 py-3"><p class="text-xs text-blue-200">Usuários</p><p class="mt-1 text-2xl font-bold">{{ totalUsers() }}</p></article>
        <article class="rounded-2xl border border-white/15 bg-white/10 px-4 py-3"><p class="text-xs text-blue-200">Redações</p><p class="mt-1 text-2xl font-bold">{{ totalCorrections() }}</p></article>
        <article class="rounded-2xl border border-white/15 bg-white/10 px-4 py-3"><p class="text-xs text-blue-200">Assinaturas ativas</p><p class="mt-1 text-2xl font-bold">{{ activeSubscriptions() }}</p></article>
        <article class="rounded-2xl border border-white/15 bg-white/10 px-4 py-3"><p class="text-xs text-blue-200">Receita total</p><p class="mt-1 text-2xl font-bold">{{ formatCurrency(totalRevenueCents()) }}</p></article>
      </div>
    </header>

    <nav class="rounded-2xl border border-slate-200/80 bg-white p-2 shadow-card">
      <div class="flex gap-2 overflow-x-auto">
        @for (item of navItems; track item.key) {
          <button type="button" (click)="setView(item.key)" class="shrink-0 rounded-xl border px-3 py-2 text-sm font-semibold"
            [class.border-blue-500]="isViewActive(item.key)"
            [class.bg-blue-50]="isViewActive(item.key)"
            [class.text-blue-900]="isViewActive(item.key)"
            [class.border-slate-200]="!isViewActive(item.key)"
            [class.text-slate-700]="!isViewActive(item.key)">
            {{ item.label }}
            @if (getNavBadge(item.key) > 0) { <span class="ml-1 rounded-full bg-slate-900 px-1.5 py-0.5 text-xs text-white">{{ getNavBadge(item.key) }}</span> }
          </button>
        }
      </div>
    </nav>

    @if (setupError()) {
      <section class="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ setupError() }}
      </section>
    }

    @if (isInitialLoading()) {
      <section class="rounded-2xl border border-slate-200/80 bg-white p-8 text-center shadow-card">
        <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-blue-500"></div>
        <p class="mt-4 text-sm font-semibold text-slate-700">Carregando dados administrativos...</p>
      </section>
    } @else {
      @switch (currentView()) {
        @case ('overview') {
          <section class="grid gap-4 lg:grid-cols-3">
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 class="text-lg font-bold text-slate-900">Ações rápidas</h2>
              <div class="mt-4 space-y-2">
                <button type="button" (click)="setView('users')" class="w-full rounded-xl bg-slate-900 px-3 py-2 text-left text-sm font-semibold text-white">Gerenciar usuários</button>
                <button type="button" (click)="setView('finance')" class="w-full rounded-xl bg-indigo-600 px-3 py-2 text-left text-sm font-semibold text-white">Financeiro e créditos</button>
                <button type="button" (click)="setView('corrections')" class="w-full rounded-xl bg-blue-600 px-3 py-2 text-left text-sm font-semibold text-white">Revisar redações</button>
                <button type="button" (click)="setView('plans')" class="w-full rounded-xl bg-emerald-600 px-3 py-2 text-left text-sm font-semibold text-white">Assinaturas e planos</button>
                <button type="button" (click)="setView('settings')" class="w-full rounded-xl bg-amber-500 px-3 py-2 text-left text-sm font-semibold text-slate-900">Configurações e logs</button>
              </div>
            </article>
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 class="text-lg font-bold text-slate-900">Indicadores</h2>
              <div class="mt-4 space-y-2 text-sm">
                <div class="flex items-center justify-between rounded-xl bg-slate-100 px-3 py-2"><span>Denúncias pendentes</span><strong>{{ pendingReports() }}</strong></div>
                <div class="flex items-center justify-between rounded-xl bg-slate-100 px-3 py-2"><span>Pagamentos pendentes</span><strong>{{ pendingSubscriptionsCount() }}</strong></div>
                <div class="flex items-center justify-between rounded-xl bg-slate-100 px-3 py-2"><span>Créditos em circulação</span><strong>{{ totalCreditsInBase() }}</strong></div>
                <div class="flex items-center justify-between rounded-xl bg-slate-100 px-3 py-2"><span>Notas abaixo de 6</span><strong>{{ lowScoreCorrections() }}</strong></div>
              </div>
            </article>
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 class="text-lg font-bold text-slate-900">Sessão admin</h2>
              @if (adminUser(); as user) {
                <div class="mt-4"><p class="font-semibold">{{ user.name }}</p><p class="text-xs text-slate-500">{{ user.email }}</p></div>
              }
              <div class="mt-4 rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-600">Última atualização: {{ getLastUpdatedLabel() }}</div>
            </article>
          </section>
        }
        @case ('finance') {
          <section class="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><p class="text-xs text-slate-500">Faturamento total</p><p class="mt-2 text-2xl font-bold">{{ formatCurrency(totalRevenueCents()) }}</p></article>
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><p class="text-xs text-slate-500">Faturamento do mês</p><p class="mt-2 text-2xl font-bold">{{ formatCurrency(monthlyRevenueCents()) }}</p></article>
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><p class="text-xs text-slate-500">Ticket médio</p><p class="mt-2 text-2xl font-bold">{{ formatCurrency(averageTicketCents()) }}</p></article>
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><p class="text-xs text-slate-500">Pagamentos pendentes</p><p class="mt-2 text-2xl font-bold">{{ pendingSubscriptionsCount() }}</p></article>
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><p class="text-xs text-slate-500">Cupons ativos</p><p class="mt-2 text-2xl font-bold">{{ activeCouponsCount() }}</p></article>
          </section>

          <section class="grid gap-4 lg:grid-cols-5">
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2 space-y-3">
              <h2 class="text-lg font-bold text-slate-900">Adicionar/remover créditos</h2>
              <select class="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" [value]="selectedFinanceUserId()" (change)="onFinanceUserChange($event)">
                <option value="">Selecione um usuário</option>
                @for (user of users(); track user.id) {
                  <option [value]="user.id">{{ user.name }} ({{ user.email }})</option>
                }
              </select>
              <input type="number" class="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" [value]="creditAdjustmentInput()" (input)="onCreditAdjustmentInputChange($event)" />
              @if (selectedFinanceUser(); as selectedUser) {
                <p class="rounded-xl bg-slate-100 px-3 py-2 text-sm">Saldo atual: <strong>{{ selectedUser.credits }}</strong> créditos</p>
              }
              <button type="button" (click)="applyCreditAdjustment()" [disabled]="isApplyingCreditAdjustment()" class="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                @if (isApplyingCreditAdjustment()) { Aplicando... } @else { Aplicar ajuste }
              </button>
            </article>
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-3">
              <div class="flex items-center justify-between">
                <h2 class="text-lg font-bold text-slate-900">Movimentações recentes</h2>
                <button type="button" (click)="setView('plans')" class="text-xs font-semibold text-blue-600 hover:underline">Ver assinaturas completas</button>
              </div>
              <div class="mt-4 space-y-2">
                @for (subscription of recentFinancialEvents(); track subscription.id) {
                  <div class="flex items-center justify-between rounded-xl bg-slate-100 px-3 py-2">
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold">{{ subscription.profiles?.name || 'Usuário sem nome' }}</p>
                      <p class="truncate text-xs text-slate-500">{{ subscription.plans?.name || 'Plano' }} • {{ formatDateTime(subscription.created_at) }}</p>
                    </div>
                    <div class="text-right">
                      <p class="text-sm font-bold">{{ formatCurrency(getSubscriptionPriceCents(subscription)) }}</p>
                      <p class="text-xs text-slate-500">{{ getSubscriptionStatusLabel(subscription.status) }}</p>
                    </div>
                  </div>
                } @empty {
                  <p class="text-sm text-slate-500">Sem movimentações recentes.</p>
                }
              </div>
            </article>
          </section>

          <section class="grid gap-4 lg:grid-cols-5">
            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2 space-y-3">
              <h2 class="text-lg font-bold text-slate-900">Criar cupom</h2>
              <input type="text" class="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm uppercase" placeholder="Código (ex.: INFLUENCER10)" [value]="couponCodeInput()" (input)="onCouponCodeInput($event)" />
              <input type="text" class="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" placeholder="Descrição (opcional)" [value]="couponDescriptionInput()" (input)="onCouponDescriptionInput($event)" />
              <div class="grid grid-cols-2 gap-2">
                <select class="rounded-xl border border-slate-300 px-3 py-2 text-sm" [value]="couponTypeInput()" (change)="onCouponTypeChange($event)">
                  <option value="percent">Percentual (%)</option>
                  <option value="fixed">Valor fixo (centavos)</option>
                </select>
                <input type="number" min="1" class="rounded-xl border border-slate-300 px-3 py-2 text-sm" [value]="couponValueInput()" (input)="onCouponValueInput($event)" />
              </div>
              <input type="number" min="1" class="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" placeholder="Limite de usos (opcional)" [value]="couponMaxUsesInput() ?? ''" (input)="onCouponMaxUsesInput($event)" />
              <input type="datetime-local" class="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" [value]="couponEndsAtInput()" (input)="onCouponEndsAtInput($event)" />
              <button type="button" (click)="createCoupon()" [disabled]="isCreatingCoupon()" class="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                @if (isCreatingCoupon()) { Criando... } @else { Criar cupom }
              </button>
            </article>

            <article class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-3">
              <h2 class="text-lg font-bold text-slate-900">Cupons cadastrados</h2>
              <div class="mt-4 space-y-2">
                @for (coupon of coupons(); track coupon.id) {
                  <div class="flex flex-col gap-3 rounded-xl bg-slate-100 px-3 py-3 md:flex-row md:items-center md:justify-between">
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold text-slate-900">{{ coupon.code }}</p>
                      <p class="text-xs text-slate-500">{{ getCouponDiscountLabel(coupon) }} • Usos: {{ getCouponUsageLabel(coupon) }}</p>
                      <p class="truncate text-xs text-blue-600">https://www.usecorrectify.com/?cupom={{ coupon.code }}</p>
                      @if (coupon.ends_at) {
                        <p class="text-xs text-slate-500">Expira em {{ formatDateTime(coupon.ends_at) }}</p>
                      }
                    </div>
                    <button
                      type="button"
                      (click)="toggleCouponStatus(coupon)"
                      [disabled]="couponActionInFlight() === coupon.id"
                      class="rounded-xl px-3 py-2 text-xs font-bold text-white disabled:opacity-60"
                      [class.bg-rose-600]="coupon.is_active"
                      [class.hover:bg-rose-500]="coupon.is_active"
                      [class.bg-emerald-600]="!coupon.is_active"
                      [class.hover:bg-emerald-500]="!coupon.is_active"
                    >
                      @if (couponActionInFlight() === coupon.id) {
                        Atualizando...
                      } @else if (coupon.is_active) {
                        Desativar
                      } @else {
                        Ativar
                      }
                    </button>
                  </div>
                } @empty {
                  <p class="text-sm text-slate-500">Nenhum cupom cadastrado ainda.</p>
                }
              </div>
            </article>
          </section>
        }
        @case ('users') {
          <app-user-management [users]="users()" (selectUser)="handleSelectUser($event)" (userDelete)="handleUserDelete($event)" (userCreate)="handleUserCreate($event)"></app-user-management>
        }
        @case ('editUser') {
          <app-user-details [user]="selectedUser()" (userUpdate)="handleUserUpdate($event)" (userDelete)="handleUserDelete($event)" (back)="handleBackToList()"></app-user-details>
        }
        @case ('corrections') {
          <app-corrections-management [corrections]="allCorrections()" (viewCorrection)="handleViewCorrection($event)"></app-corrections-management>
        }
        @case ('viewCorrection') {
          <div class="space-y-4">
            <button type="button" (click)="handleBackToCorrections()" class="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100">
              Voltar para redações
            </button>
            @if (selectedCorrectionWithUser()) {
              <app-correction-report [report]="selectedCorrectionWithUser()!.data" [correctionId]="selectedCorrectionWithUser()!.id" (newCorrection)="handleBackToCorrections()"></app-correction-report>
            }
          </div>
        }
        @case ('plans') {
          <app-plans-management [plans]="plans()" [subscriptions]="subscriptions()" (planCreate)="handlePlanCreate($event)" (planUpdate)="handlePlanUpdate($event)"></app-plans-management>
        }
        @case ('settings') {
          <app-settings></app-settings>
        }
      }
    }
  </div>
</div>
`,
})
export class AdminDashboardComponent {
  private adminService = inject(AdminService);
  private authService = inject(AuthService);
  private logService = inject(LogService);

  readonly navItems: Array<{ key: AdminPrimaryView; label: string }> = [
    { key: 'overview', label: 'Visão geral' },
    { key: 'finance', label: 'Financeiro' },
    { key: 'users', label: 'Usuários' },
    { key: 'corrections', label: 'Redações' },
    { key: 'plans', label: 'Planos' },
    { key: 'settings', label: 'Configurações' },
  ];

  readonly currentView = signal<AdminView>('overview');
  readonly selectedUser = signal<User | null>(null);
  readonly selectedCorrectionWithUser = signal<CorrectionWithUser | null>(null);
  readonly pendingDeleteUserId = signal<string | null>(null);
  readonly selectedFinanceUserId = signal('');
  readonly creditAdjustmentInput = signal(1);
  readonly isApplyingCreditAdjustment = signal(false);

  readonly stats = signal<AdminStatistics | null>(null);
  readonly users = signal<User[]>([]);
  readonly allCorrections = signal<CorrectionWithUser[]>([]);
  readonly plans = signal<Plan[]>([]);
  readonly coupons = signal<DiscountCoupon[]>([]);
  readonly subscriptions = signal<SubscriptionWithDetails[]>([]);
  readonly setupError = signal<string | null>(null);
  readonly isBusy = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly lastUpdatedAt = signal<Date | null>(null);
  readonly adminUser = this.authService.currentUser;

  readonly isInitialLoading = computed(() => !this.hasLoadedOnce() && this.isBusy());
  readonly totalUsers = computed(() => this.stats()?.totalUsers ?? this.users().length);
  readonly totalCorrections = computed(() => this.stats()?.totalCorrections ?? this.allCorrections().length);
  readonly activeSubscriptions = computed(() => this.subscriptions().filter((s) => s.status === 'active').length);
  readonly pendingSubscriptionsCount = computed(() => this.subscriptions().filter((s) => s.status === 'pending').length);
  readonly pendingReports = computed(() => this.allCorrections().filter((c) => c.status === 'reported').length);
  readonly lowScoreCorrections = computed(() => this.allCorrections().filter((c) => Number(c.data?.notaFinal ?? 0) < 6).length);
  readonly totalCreditsInBase = computed(() => this.users().reduce((sum, user) => sum + Math.max(0, Number(user.credits ?? 0)), 0));
  readonly planPriceById = computed(() => {
    const map = new Map<number, number>();
    for (const plan of this.plans()) map.set(plan.id, Number(plan.price ?? 0));
    return map;
  });
  readonly approvedSubscriptions = computed(() => this.subscriptions().filter((s) => s.status !== 'pending'));
  readonly totalRevenueCents = computed(() => this.approvedSubscriptions().reduce((sum, sub) => sum + this.getSubscriptionPriceCents(sub), 0));
  readonly monthlyRevenueCents = computed(() => {
    const now = new Date();
    return this.approvedSubscriptions().reduce((sum, sub) => {
      const createdAt = new Date(sub.created_at);
      if (Number.isNaN(createdAt.getTime())) return sum;
      const sameMonth = createdAt.getUTCMonth() === now.getUTCMonth() && createdAt.getUTCFullYear() === now.getUTCFullYear();
      return sameMonth ? sum + this.getSubscriptionPriceCents(sub) : sum;
    }, 0);
  });
  readonly payingUsersCount = computed(() => new Set(this.approvedSubscriptions().map((s) => s.user_id)).size);
  readonly averageTicketCents = computed(() => (this.payingUsersCount() > 0 ? Math.round(this.totalRevenueCents() / this.payingUsersCount()) : 0));
  readonly recentFinancialEvents = computed(() =>
    [...this.subscriptions()].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 8)
  );
  readonly activeCouponsCount = computed(() => this.coupons().filter((coupon) => coupon.is_active).length);
  readonly selectedFinanceUser = computed(() => this.users().find((u) => u.id === this.selectedFinanceUserId()) ?? null);
  readonly couponCodeInput = signal('');
  readonly couponDescriptionInput = signal('');
  readonly couponTypeInput = signal<'percent' | 'fixed'>('percent');
  readonly couponValueInput = signal(10);
  readonly couponMaxUsesInput = signal<number | null>(null);
  readonly couponEndsAtInput = signal('');
  readonly isCreatingCoupon = signal(false);
  readonly couponActionInFlight = signal<number | null>(null);
  readonly viewHint = computed(() => {
    switch (this.currentView()) {
      case 'finance':
        return 'Acompanhe faturamento, pagamentos e ajuste créditos de usuários.';
      case 'users':
      case 'editUser':
        return 'Gerencie perfis, créditos e dados da base de usuários.';
      case 'corrections':
      case 'viewCorrection':
        return 'Monitore qualidade das correções e denúncias.';
      case 'plans':
        return 'Controle planos, assinaturas e status de cobrança.';
      case 'settings':
        return 'Ajuste integrações e operação interna da plataforma.';
      default:
        return 'Visibilidade do negócio com dados consolidados e atalhos operacionais.';
    }
  });

  constructor() {
    void this.refreshAllData();
  }

  isViewActive(view: AdminPrimaryView): boolean {
    const current = this.currentView();
    if (view === 'users') return current === 'users' || current === 'editUser';
    if (view === 'corrections') return current === 'corrections' || current === 'viewCorrection';
    return current === view;
  }

  getNavBadge(view: AdminPrimaryView): number {
    switch (view) {
      case 'users': return this.totalUsers();
      case 'finance': return this.pendingSubscriptionsCount();
      case 'corrections': return this.pendingReports();
      case 'plans': return this.activeSubscriptions();
      default: return 0;
    }
  }

  setView(view: AdminPrimaryView | AdminView) {
    this.currentView.set(view as AdminView);
  }

  async refreshAllData(showSuccessToast = false) {
    if (this.isBusy()) return;
    this.isBusy.set(true);
    this.setupError.set(null);
    try {
      const [statsData, usersData, correctionsData, plansData, subscriptionsData, couponsData] = await Promise.all([
        this.adminService.getStatistics(),
        this.adminService.getUsers(),
        this.adminService.getCorrections(),
        this.adminService.getPlans(),
        this.adminService.getSubscriptions(),
        this.adminService.getCoupons(),
      ]);
      this.stats.set(statsData);
      this.users.set(usersData);
      this.allCorrections.set(correctionsData);
      this.plans.set(plansData);
      this.subscriptions.set(subscriptionsData);
      this.coupons.set(couponsData);
      this.lastUpdatedAt.set(new Date());
      if (!this.selectedFinanceUserId() && usersData.length > 0) this.selectedFinanceUserId.set(usersData[0].id);
      if (showSuccessToast) this.authService.showNotification('success', 'Painel atualizado', 'Os dados administrativos foram sincronizados.');
    } catch (error) {
      await this.logService.logError('Admin dashboard refresh failed', error, {
        section: 'refreshAllData',
      });
      this.setupError.set('Não foi possível carregar todos os dados administrativos neste momento.');
      this.authService.showNotification('error', 'Falha ao carregar painel', 'Não foi possível atualizar os dados do admin agora.');
    } finally {
      this.hasLoadedOnce.set(true);
      this.isBusy.set(false);
    }
  }

  async reloadUsersAndStats() {
    const [usersData, statsData] = await Promise.all([this.adminService.getUsers(), this.adminService.getStatistics()]);
    this.users.set(usersData);
    this.stats.set(statsData);
    this.lastUpdatedAt.set(new Date());
    if (!usersData.find((user) => user.id === this.selectedFinanceUserId()) && usersData.length > 0) {
      this.selectedFinanceUserId.set(usersData[0].id);
    }
  }

  async reloadPlansAndSubscriptions() {
    const [plansData, subscriptionsData, couponsData] = await Promise.all([
      this.adminService.getPlans(),
      this.adminService.getSubscriptions(),
      this.adminService.getCoupons(),
    ]);
    this.plans.set(plansData);
    this.subscriptions.set(subscriptionsData);
    this.coupons.set(couponsData);
    this.lastUpdatedAt.set(new Date());
  }

  onFinanceUserChange(event: Event) {
    this.selectedFinanceUserId.set((event.target as HTMLSelectElement).value.trim());
  }

  onCreditAdjustmentInputChange(event: Event) {
    const numeric = Number((event.target as HTMLInputElement).value);
    this.creditAdjustmentInput.set(Number.isFinite(numeric) ? Math.trunc(numeric) : 0);
  }

  async applyCreditAdjustment() {
    const user = this.selectedFinanceUser();
    const delta = Math.trunc(this.creditAdjustmentInput());
    if (!user) {
      this.authService.showNotification('info', 'Selecione um usuário', 'Escolha o usuário antes de aplicar ajuste de créditos.');
      return;
    }
    if (!Number.isFinite(delta) || delta === 0) {
      this.authService.showNotification('info', 'Ajuste inválido', 'Informe um valor diferente de zero para adicionar ou remover créditos.');
      return;
    }
    this.isApplyingCreditAdjustment.set(true);
    try {
      await this.adminService.adjustUserCredits(user.id, delta);
      await this.reloadUsersAndStats();
      const action = delta > 0 ? 'adicionados' : 'removidos';
      this.authService.showNotification('success', 'Créditos atualizados', `${Math.abs(delta)} créditos ${action} para ${user.name}.`);
    } catch (error) {
      await this.logService.logError('Admin credit adjustment failed', error, {
        userId: user.id,
        delta,
      });
      this.authService.showNotification('error', 'Falha ao ajustar créditos', 'Não foi possível atualizar os créditos deste usuário.');
    } finally {
      this.isApplyingCreditAdjustment.set(false);
    }
  }

  onCouponCodeInput(event: Event) {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.couponCodeInput.set(value.toUpperCase());
  }

  onCouponDescriptionInput(event: Event) {
    this.couponDescriptionInput.set((event.target as HTMLInputElement).value ?? '');
  }

  onCouponTypeChange(event: Event) {
    const value = ((event.target as HTMLSelectElement).value ?? 'percent') as 'percent' | 'fixed';
    this.couponTypeInput.set(value === 'fixed' ? 'fixed' : 'percent');
  }

  onCouponValueInput(event: Event) {
    const numeric = Number((event.target as HTMLInputElement).value);
    this.couponValueInput.set(Number.isFinite(numeric) ? Math.max(1, Math.trunc(numeric)) : 1);
  }

  onCouponMaxUsesInput(event: Event) {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (!raw) {
      this.couponMaxUsesInput.set(null);
      return;
    }
    const numeric = Number(raw);
    this.couponMaxUsesInput.set(Number.isFinite(numeric) ? Math.max(1, Math.trunc(numeric)) : null);
  }

  onCouponEndsAtInput(event: Event) {
    this.couponEndsAtInput.set((event.target as HTMLInputElement).value ?? '');
  }

  private resetCouponForm() {
    this.couponCodeInput.set('');
    this.couponDescriptionInput.set('');
    this.couponTypeInput.set('percent');
    this.couponValueInput.set(10);
    this.couponMaxUsesInput.set(null);
    this.couponEndsAtInput.set('');
  }

  async createCoupon() {
    const normalizedCode = this.couponCodeInput()
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9_-]/g, '')
      .slice(0, 40);

    if (!normalizedCode) {
      this.authService.showNotification('info', 'Código inválido', 'Informe um código de cupom válido.');
      return;
    }

    this.isCreatingCoupon.set(true);
    try {
      const payload: CreateDiscountCouponInput = {
        code: normalizedCode,
        description: this.couponDescriptionInput().trim(),
        discount_type: this.couponTypeInput(),
        discount_value: Math.max(1, Math.trunc(this.couponValueInput())),
        max_uses: this.couponMaxUsesInput(),
        starts_at: null,
        ends_at: this.couponEndsAtInput() ? new Date(this.couponEndsAtInput()).toISOString() : null,
      };

      await this.adminService.createCoupon(payload);
      await this.reloadPlansAndSubscriptions();
      this.resetCouponForm();
      this.authService.showNotification('success', 'Cupom criado', `Cupom ${normalizedCode} criado com sucesso.`);
    } catch (error) {
      await this.logService.logError('Admin coupon creation failed', error, { code: normalizedCode });
      this.authService.showNotification('error', 'Falha ao criar cupom', 'Não foi possível criar este cupom agora.');
    } finally {
      this.isCreatingCoupon.set(false);
    }
  }

  async toggleCouponStatus(coupon: DiscountCoupon) {
    if (this.couponActionInFlight() === coupon.id) {
      return;
    }

    this.couponActionInFlight.set(coupon.id);
    try {
      await this.adminService.updateCoupon(coupon.id, { is_active: !coupon.is_active });
      await this.reloadPlansAndSubscriptions();
      this.authService.showNotification(
        'success',
        'Cupom atualizado',
        `Cupom ${coupon.code} ${coupon.is_active ? 'desativado' : 'ativado'} com sucesso.`
      );
    } catch (error) {
      await this.logService.logError('Admin coupon toggle failed', error, { couponId: coupon.id, code: coupon.code });
      this.authService.showNotification('error', 'Falha ao atualizar cupom', 'Não foi possível alterar o status deste cupom.');
    } finally {
      this.couponActionInFlight.set(null);
    }
  }

  handleSelectUser(user: User) {
    this.selectedUser.set(user);
    this.currentView.set('editUser');
  }

  async handleUserCreate(newUser: Pick<User, 'name' | 'email' | 'credits' | 'plan'>) {
    try {
      await this.adminService.addUser(newUser);
      await this.reloadUsersAndStats();
      this.authService.showNotification('success', 'Usuário criado', `Novo usuário "${newUser.name}" adicionado.`);
    } catch (error) {
      await this.logService.logError('Admin user creation failed', error, {
        email: newUser.email,
        plan: newUser.plan,
      });
      this.authService.showNotification('error', 'Falha ao criar usuário', 'Não foi possível criar o usuário agora.');
    }
  }

  async handleUserUpdate(user: User) {
    try {
      await this.adminService.updateUser(user);
      await this.reloadUsersAndStats();
      this.currentView.set('users');
      this.authService.showNotification('success', 'Usuário atualizado', `Dados de "${user.name}" atualizados.`);
    } catch (error) {
      await this.logService.logError('Admin user update failed', error, {
        userId: user.id,
        email: user.email,
      });
      this.authService.showNotification('error', 'Falha ao atualizar usuário', 'Não foi possível salvar as alterações.');
    }
  }

  async handleUserDelete(userId: string) {
    if (this.pendingDeleteUserId() !== userId) {
      this.pendingDeleteUserId.set(userId);
      this.authService.showNotification('info', 'Confirmar exclusão', 'Clique novamente em ate 5 segundos para remover este usuário.');
      setTimeout(() => {
        if (this.pendingDeleteUserId() === userId) this.pendingDeleteUserId.set(null);
      }, 5000);
      return;
    }
    this.pendingDeleteUserId.set(null);
    try {
      await this.adminService.deleteUser(userId);
      await this.reloadUsersAndStats();
      this.currentView.set('users');
      this.authService.showNotification('success', 'Usuário removido', 'O usuário foi removido com sucesso.');
    } catch (error) {
      await this.logService.logError('Admin user deletion failed', error, {
        userId,
      });
      this.authService.showNotification('error', 'Falha ao remover usuário', 'Não foi possível remover este usuário.');
    }
  }

  handleBackToList() {
    this.selectedUser.set(null);
    this.currentView.set('users');
  }

  handleViewCorrection(correction: CorrectionWithUser) {
    this.selectedCorrectionWithUser.set(correction);
    this.currentView.set('viewCorrection');
  }

  handleBackToCorrections() {
    this.selectedCorrectionWithUser.set(null);
    this.currentView.set('corrections');
  }

  async handlePlanCreate(planData: Omit<Plan, 'id' | 'created_at'>) {
    try {
      await this.adminService.createPlan(planData);
      await this.reloadPlansAndSubscriptions();
      this.authService.showNotification('success', 'Plano criado', `Plano "${planData.name}" criado com sucesso.`);
    } catch (error) {
      await this.logService.logError('Admin plan creation failed', error, {
        planName: planData.name,
      });
      this.authService.showNotification('error', 'Falha ao criar plano', 'Não foi possível criar o plano.');
    }
  }

  async handlePlanUpdate(plan: Plan) {
    try {
      await this.adminService.updatePlan(plan);
      await this.reloadPlansAndSubscriptions();
      this.authService.showNotification('success', 'Plano atualizado', `Plano "${plan.name}" atualizado.`);
    } catch (error) {
      await this.logService.logError('Admin plan update failed', error, {
        planId: plan.id,
        planName: plan.name,
      });
      this.authService.showNotification('error', 'Falha ao atualizar plano', 'Não foi possível atualizar o plano.');
    }
  }

  logout() {
    this.authService.showNotification('info', 'Sessão encerrada', 'Você saiu da conta de administrador.');
    void this.authService.signOut();
  }

  getSubscriptionPriceCents(subscription: SubscriptionWithDetails): number {
    return Number(this.planPriceById().get(subscription.plan_id) ?? 0);
  }

  getSubscriptionStatusLabel(status: SubscriptionWithDetails['status']): string {
    switch (status) {
      case 'active': return 'Ativa';
      case 'pending': return 'Pendente';
      case 'canceled': return 'Cancelada';
      case 'expired': return 'Expirada';
      default: return 'N/A';
    }
  }

  getCouponDiscountLabel(coupon: DiscountCoupon): string {
    if (coupon.discount_type === 'percent') {
      return `${coupon.discount_value}%`;
    }
    return this.formatCurrency(coupon.discount_value);
  }

  getCouponUsageLabel(coupon: DiscountCoupon): string {
    if (coupon.max_uses === null) {
      return `${coupon.uses_count} / ilimitado`;
    }
    return `${coupon.uses_count} / ${coupon.max_uses}`;
  }

  formatCurrency(cents: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((Number(cents) || 0) / 100);
  }

  getLastUpdatedLabel(): string {
    const last = this.lastUpdatedAt();
    if (!last) return 'nunca';
    return last.toLocaleString('pt-BR');
  }

  formatDateTime(raw: string): string {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR');
  }
}
